import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/common/utils/cn"
import { twMerge } from "tailwind-merge"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] bg-black shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}

    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  children,
  showClose = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { showClose?: boolean }) => (
  <div
    className={cn(
      "flex items-center justify-between text-center sm:text-left bg-white-200 p-6 rounded-t-md",
      className
    )}
    {...props}
  >
    <div>
      {children}
    </div>
    {showClose && (
      <DialogPrimitive.Close className="">
        <X className="h-6 w-6" color="red" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    )}
  </div>
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  dataTestid,
  ...props
}: React.HTMLAttributes<HTMLButtonElement> & { dataTestid?: string }) => (
  <button
    data-testid={dataTestid}
    className={cn(
      "flex justify-center items-center bg-white-800 py-3 text-center text-black rounded-sm",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogConfirmFooter = ({
  cancelButton,
  confirmButton,
}: {
  cancelButton: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  confirmButton: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
}) => {
  const { className: cancelButtonClassName, ...cancelButtonProps } = cancelButton;
  const { className: confirmButtonClassName, ...confirmButtonProps } = confirmButton;
  return (
    <div className="flex">
      <DialogPrimitive.Close
        className={twMerge('w-1/2 rounded-sm rounded-es-lg flex flex-col', cancelButtonClassName)}
      >
        <button
          {...cancelButtonProps}
          className="flex-1"
        />
      </DialogPrimitive.Close>

      <button
        className={twMerge('bg-danger rounded-sm rounded-ee-lg w-1/2 py-4', confirmButtonClassName)}
        {...confirmButtonProps}
      />
    </div>
  )
}
DialogConfirmFooter.displayName = "DialogConfirmFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground p-6", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogConfirmFooter
}
